/**
 * @voltxampere/schema — Migration 0.2.0 → 0.3.0 (Sprint 8c)
 *
 * Sprint 8c aligns the BJT parameter names with the SPICE / ngspice
 * convention and flips the default value that was the root cause of
 * the V_BC=0 knee asymmetry identified in Sprint 8b.1's diagnostic
 * (see `docs/sprints/diagnostics/sprint-8c-pnp-diagnostic.md` Test 1):
 *
 *     Ne (BE ideality, default 1)  →  Nf (forward emission, default 1)
 *     Nc (BC ideality, default 2)  →  Nr (reverse emission, default 1)
 *
 * ngspice's conventional `.model ... NPN (NF=1 NR=1 …)` keeps both
 * emission coefficients at 1 and reserves the name `NC` for the
 * Gummel-Poon collector leakage saturation emission coefficient —
 * a parameter the Sprint 7 injection-only Ebers-Moll does not model
 * at all. The v3 Sprint 7 schema accidentally reused that `NC` slot
 * for the *reverse-injection* ideality factor with default 2, which
 * over-damps the reverse current term and produces a 6% residual
 * at ngspice's authoritative PNP CE operating point.
 *
 * Migration policy for circuits saved under 0.2.0:
 *   - `params.Ne`  → `params.Nf`                  (value preserved)
 *   - `params.Nc`  → `params.Nr`                  (value preserved)
 *   - Missing `Nc` stays missing                  (consumes new default 1)
 *
 * The value-preservation choice is deliberate: a circuit saved with
 * an explicit `Nc: 2` probably was tuned for that physics; silently
 * rewriting to `Nr: 1` would change the user's simulation. The new
 * default only kicks in for circuits that *never* specified the
 * field (the overwhelming majority, per Sprint 7 fixture audits).
 *
 * Affected types: `bjt-npn`, `bjt-pnp`. All other component types
 * pass through unchanged.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const BJT_TYPES = new Set(["bjt-npn", "bjt-pnp"]);
function migrateComponent(c) {
    if (typeof c !== "object" || c === null)
        return c;
    const comp = c;
    const type = comp.type;
    if (typeof type !== "string" || !BJT_TYPES.has(type))
        return c;
    if (typeof comp.params !== "object" || comp.params === null)
        return c;
    const params = comp.params;
    // Nothing to rewrite? Pass through (very common for default-only
    // fixtures — the schema defaults handle Nf=1/Nr=1 transparently).
    const hasNe = Object.prototype.hasOwnProperty.call(params, "Ne");
    const hasNc = Object.prototype.hasOwnProperty.call(params, "Nc");
    if (!hasNe && !hasNc)
        return c;
    const next = { ...params };
    if (hasNe) {
        next.Nf = next.Ne;
        delete next.Ne;
    }
    if (hasNc) {
        next.Nr = next.Nc;
        delete next.Nc;
    }
    return { ...comp, params: next };
}
export const migration_0_2_0_to_0_3_0 = {
    from: "0.2.0",
    to: "0.3.0",
    description: "Sprint 8c: BJT Ne/Nc → Nf/Nr rename (ngspice-aligned forward/reverse emission coefficients)",
    migrate(circuit) {
        if (typeof circuit !== "object" || circuit === null) {
            return circuit;
        }
        const raw = circuit;
        const components = Array.isArray(raw.components)
            ? raw.components.map(migrateComponent)
            : raw.components;
        return { ...raw, schemaVersion: "0.3.0", components };
    },
};
//# sourceMappingURL=0.2.0-to-0.3.0.js.map